// File: frontend/src/components/admin/DashboardChartSection.tsx

import React from "react";
import { useTranslation } from "react-i18next";
import {
  AreaChart, Area,
  BarChart, Bar,
  LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import { FaChartArea, FaChartBar, FaChartLine } from "react-icons/fa";
import {
  DashboardMetric,
  DashboardChartPeriod,
  DashboardChartType,
  DashboardChartPoint,
} from "../../services/adminDashboardService";
import { formatCompactNumber, formatFullNumber } from "../../utils";

const PRIMARY  = "var(--color-primary)";
const BG_ENTRY = "var(--bg-entry)";
const TEXT     = "var(--text-primary)";

const CHART_TICK_COUNT = 12;

function formatLabel(label: string, period: DashboardChartPeriod): string {
  if (!label) return "";
  if (period === "today") return `${label}:00`;
  if (period === "year" || period === "thisyear" || period === "alltime") {
    const [y, m] = label.split("-");
    const d = new Date(Number(y), Number(m) - 1, 1);
    return d.toLocaleDateString("tr-TR", { month: "short" }) + " '" + y.slice(2);
  }
  const [y, m, d] = label.split("-");
  const dt = new Date(Number(y), Number(m) - 1, Number(d));
  return dt.toLocaleDateString("tr-TR", { day: "numeric", month: "short" });
}

function makeAxes(period: DashboardChartPeriod, dataLen: number): React.ReactElement[] {
  const dense = period === "year" || period === "thisyear" || period === "alltime";
  let interval: number;
  if (period === "today") interval = 3;
  else if (period === "month") interval = 4;
  else if (period === "alltime") interval = Math.max(0, Math.floor(dataLen / CHART_TICK_COUNT) - 1);
  else interval = 0;

  return [
    <CartesianGrid key="grid" strokeDasharray="3 3" stroke="#d9d9d9" vertical horizontal />,
    <XAxis
      key="xaxis"
      dataKey="displayLabel"
      tick={{ fontSize: 11, fill: "#555" }}
      angle={dense ? -35 : 0}
      textAnchor={dense ? "end" : "middle"}
      tickLine={{ stroke: "#ccc" }}
      axisLine={{ stroke: "#ccc" }}
      interval={interval}
      height={dense ? 52 : 28}
    />,
    // Y-axis ticks are shortened ("1,2k", "3,4M") so large values keep fitting
    // in the fixed axis width as the platform grows.
    <YAxis
      key="yaxis"
      tick={{ fontSize: 11, fill: "#555" }}
      tickLine={{ stroke: "#ccc" }}
      axisLine={{ stroke: "#ccc" }}
      allowDecimals={false}
      width={44}
      tickFormatter={(v) => formatCompactNumber(Number(v))}
    />,
    <Tooltip
      key="tooltip"
      contentStyle={{ background: BG_ENTRY, border: "1px solid #ddd", borderRadius: "var(--radius-sm)", fontSize: "12px", color: TEXT }}
      labelStyle={{ fontWeight: 600 }}
      cursor={{ stroke: PRIMARY, strokeWidth: 1, strokeDasharray: "4 4" }}
      formatter={(value) => formatFullNumber(Number(value))}
    />,
  ];
}

function renderChart(type: DashboardChartType, data: object[], metricLabel: string, period: DashboardChartPeriod) {
  const axes = makeAxes(period, data.length);
  if (type === "bar") {
    return (
      <BarChart data={data} margin={{ top: 8, right: 20, left: 0, bottom: 4 }}>
        {axes}
        <Bar dataKey="value" fill={PRIMARY} radius={[3, 3, 0, 0]} name={metricLabel} maxBarSize={40} />
      </BarChart>
    );
  }
  if (type === "line") {
    return (
      <LineChart data={data} margin={{ top: 8, right: 20, left: 0, bottom: 4 }}>
        {axes}
        <Line type="stepAfter" dataKey="value" stroke={PRIMARY} strokeWidth={2} dot={false} activeDot={{ r: 5, stroke: BG_ENTRY, strokeWidth: 2, fill: PRIMARY }} name={metricLabel} />
      </LineChart>
    );
  }
  return (
    <AreaChart data={data} margin={{ top: 8, right: 20, left: 0, bottom: 4 }}>
      <defs>
        <linearGradient id="colorDash" x1="0" y1="0" x2="0" y2="1">
          <stop offset="5%"  stopColor={PRIMARY} stopOpacity={0.4} />
          <stop offset="95%" stopColor={PRIMARY} stopOpacity={0.02} />
        </linearGradient>
      </defs>
      {axes}
      <Area type="monotone" dataKey="value" stroke={PRIMARY} strokeWidth={2.5} fill="url(#colorDash)" dot={false} activeDot={{ r: 5, stroke: BG_ENTRY, strokeWidth: 2, fill: PRIMARY }} name={metricLabel} />
    </AreaChart>
  );
}

interface Props {
  chartData: DashboardChartPoint[];
  loading: boolean;
  error: boolean;
  metric: DashboardMetric;
  setMetric: (v: DashboardMetric) => void;
  chartPeriod: DashboardChartPeriod;
  setChartPeriod: (v: DashboardChartPeriod) => void;
  chartType: DashboardChartType;
  setChartType: (v: DashboardChartType) => void;
  onRetry: () => void;
}

export const DashboardChartSection: React.FC<Props> = ({
  chartData, loading, error, metric, setMetric,
  chartPeriod, setChartPeriod, chartType, setChartType, onRetry,
}) => {
  const { t } = useTranslation();

  const chartPeriods: { key: DashboardChartPeriod; label: string }[] = [
    { key: "today",    label: t("dash_period_today")    },
    { key: "week",     label: t("dash_period_week")     },
    { key: "month",    label: t("dash_period_month")    },
    { key: "year",     label: t("dash_period_year")     },
    { key: "thisyear", label: t("dash_period_thisyear") },
    { key: "alltime",  label: t("dash_period_alltime")  },
  ];

  const chartMetrics: { key: DashboardMetric; label: string }[] = [
    { key: "entries", label: t("dash_chart_metric_entries") },
    { key: "users",   label: t("dash_chart_metric_users")   },
    { key: "matches", label: t("dash_chart_metric_matches") },
    { key: "clubs",   label: t("dash_chart_metric_clubs")   },
    { key: "topics",  label: t("dash_chart_metric_topics")  },
  ];

  const chartTypes: { key: DashboardChartType; icon: React.ReactNode; label: string }[] = [
    { key: "bar",  icon: <FaChartBar  />, label: t("dash_chart_type_bar")  },
    { key: "area", icon: <FaChartArea />, label: t("dash_chart_type_area") },
    { key: "line", icon: <FaChartLine />, label: t("dash_chart_type_line") },
  ];

  const formattedData = chartData.map((pt) => ({ ...pt, displayLabel: formatLabel(pt.label, chartPeriod) }));
  const metricLabel = chartMetrics.find((m) => m.key === metric)?.label ?? "";

  return (
    <div className="dash-chart-section">
      <div className="dash-chart-header">
        <h3 className="dash-chart-title">{t("dash_chart_title")}</h3>
        <div className="dash-chart-controls">
          <div className="dash-chart-type-toggle">
            {chartTypes.map((ct) => (
              <button key={ct.key} className={`dash-chart-type-btn${chartType === ct.key ? " active" : ""}`}
                onClick={() => setChartType(ct.key)} title={ct.label}>
                {ct.icon}
              </button>
            ))}
          </div>
          <div className="dash-metric-toggle">
            {chartMetrics.map((m) => (
              <button key={m.key} className={`dash-metric-btn${metric === m.key ? " active" : ""}`}
                onClick={() => setMetric(m.key)}>
                {m.label}
              </button>
            ))}
          </div>
          <div className="dash-period-toggle">
            {chartPeriods.map((p) => (
              <button key={p.key} className={`dash-period-btn${chartPeriod === p.key ? " active" : ""}`}
                onClick={() => setChartPeriod(p.key)}>
                {p.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="dash-chart-loading">{t("dash_loading")}</div>
      ) : error ? (
        <div className="dash-chart-empty">
          <span>{t("dash_error")}</span>
          <button className="dash-retry-btn" onClick={onRetry}>{t("dash_retry")}</button>
        </div>
      ) : formattedData.length === 0 ? (
        <div className="dash-chart-empty">{t("dash_no_data")}</div>
      ) : (
        <div className="dash-chart-wrapper">
          <ResponsiveContainer width="100%" height="100%">
            {renderChart(chartType, formattedData, metricLabel, chartPeriod)}
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};